import { useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import CircularProgress from '@material-ui/core/CircularProgress'

import useConversationsStore from '@/stores/useConversationsStore'
import useCurrentChatId from '@/components/hooks/use-current-chat-id'
import { PendingMessage, MessageSender, Message } from '../../models/Conversation.model'
import BotTypingIndicator from './bot-typing'
import { toast } from 'react-toastify'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMessagesFetcher } from '../../hooks/useMessagesFetcher'
import MessageTimestamp from './message-timestamp'
import BotIcon from '../../icons/chatbot-svg-icon'
import MessageAttachments from './message-attachments'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: `${theme.spacing(2)}px ${theme.spacing(8)}px ${theme.spacing(0)}px`,
    width: '100%',
  },
  messageItemWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    marginBottom: theme.spacing(1.5),
  },
  userMessageItem: {
    alignItems: 'flex-end',
  },
  botMessageItem: {
    alignItems: 'flex-start',
    width: '100%',
  },
  userMessageWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userMessage: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius * 2,
    width: 'fit-content',
    fontSize: '1.3rem',
    wordBreak: 'break-word',
    textAlign: 'left',
  },
  botMessageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    margin: '0 auto',
  },
  botMessage: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    width: '100%',
    fontSize: '1.5rem',
    lineHeight: 1.6,
    wordBreak: 'break-word',
    textAlign: 'left',
    '& table': { width: '100%', borderCollapse: 'collapse', marginBottom: theme.spacing(2) },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: 'left',
    },
    '& th': { backgroundColor: 'rgba(0, 0, 0, 0.03)', fontWeight: 'bold' },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      fontWeight: 'bold',
    },
    '& p': { margin: `0 0 ${theme.spacing(1)}px 0` },
    '& ul, & ol': { paddingLeft: theme.spacing(3), marginBottom: theme.spacing(1) },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& pre': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius,
      overflowX: 'auto',
    },
    '& code': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      padding: '0.2em 0.4em',
      margin: 0,
      fontSize: '85%',
      borderRadius: '3px',
    },
  },
  centeredMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorMessageText: {
    color: theme.palette.error.main,
  },
  botMessageWrapperRelative: {
    position: 'relative',
  },
  botAvatar: {
    position: 'absolute',
    width: theme.spacing(5),
    height: theme.spacing(5),
    top: theme.spacing(2.5),
    left: `calc(${theme.spacing(2)}px - ${theme.spacing(8)}px)`,
  },
  botHeaderLabel: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
}))

type Props = {
  pendingMessage?: PendingMessage | null
}

const ConversationHistory = ({ pendingMessage }: Props) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const currentConversationId = useCurrentChatId()

  const { isLoading: isLoadingMessages, error: messagesError } = useMessagesFetcher({
    conversationId: currentConversationId,
  })

  const currentConversation = useConversationsStore((state) =>
    currentConversationId ? state.getConversation(currentConversationId) : null
  )

  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    })
  }, [currentConversation?.messages.length, pendingMessage])

  if (
    !currentConversation &&
    !pendingMessage &&
    !isLoadingMessages &&
    !messagesError &&
    currentConversationId
  ) {
    toast.error(`Conversation with ID ${currentConversationId} not found.`)
    const basePath = location.pathname.split('/plus')[0]
    navigate(basePath + '/plus')
    return null
  }
  if (
    currentConversation &&
    isLoadingMessages &&
    currentConversation.messages.length === 0 &&
    !messagesError
  ) {
    return (
      <div className={classes.centeredMessage}>
        <CircularProgress />
      </div>
    )
  }
  if (currentConversation && messagesError && currentConversation.messages.length === 0) {
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="h6" className={classes.errorMessageText}>
          Error loading messages
        </Typography>
        <Typography variant="body2" className={classes.errorMessageText}>
          {messagesError.message}
        </Typography>
      </div>
    )
  }
  if (
    currentConversation &&
    currentConversation.messages.length === 0 &&
    !pendingMessage &&
    !isLoadingMessages &&
    !messagesError
  ) {
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="h6">No messages found in this conversation.</Typography>
        <Typography variant="body2">Start a new conversation or select an existing one.</Typography>
      </div>
    )
  }
  if (!currentConversation && pendingMessage) {
    return (
      <div className={classes.container}>
        <>
          <div className={classes.userMessageWrapper}>
            <div className={`${classes.messageItemWrapper} ${classes.userMessageItem}`}>
              <Typography className={classes.userMessage}>{pendingMessage.text}</Typography>
              {pendingMessage.attachments && (
                <MessageAttachments attachments={pendingMessage.attachments} isUserMessage={true} />
              )}
            </div>
          </div>
          <div className={`${classes.botMessageWrapper}`}>
            <div className={`${classes.messageItemWrapper} ${classes.botMessageItem}`}>
              <BotTypingIndicator />
            </div>
          </div>
        </>
        <div ref={bottomRef} />
      </div>
    )
  }
  if (!currentConversation) {
    return (
      <div className={classes.centeredMessage}>
        <Typography variant="body1">Select a conversation to view messages.</Typography>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      {currentConversation.messages.map((msg, index) => {
        const message = msg as Message

        if (message.from === MessageSender.USER) {
          return (
            <div key={message.id || index} className={classes.userMessageWrapper}>
              <div className={`${classes.messageItemWrapper} ${classes.userMessageItem}`}>
                <Typography className={classes.userMessage}>{message.text}</Typography>
                {message.attachments && (
                  <MessageAttachments attachments={message.attachments} isUserMessage={true} />
                )}
                {message.createdAt && <MessageTimestamp timestamp={message.createdAt} />}
              </div>
            </div>
          )
        } else if (message.from === MessageSender.BOT) {
          return (
            <div
              key={message.id || index}
              className={`${classes.botMessageWrapper} ${classes.botMessageWrapperRelative}`}
            >
              <BotIcon className={classes.botAvatar} />

              <div className={`${classes.messageItemWrapper} ${classes.botMessageItem}`}>
                <Typography variant="caption" className={classes.botHeaderLabel}>
                  Prodigy Plus | Ad & Marketing assistant
                </Typography>
                <div className={classes.botMessage}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                </div>
                {message.attachments && (
                  <MessageAttachments attachments={message.attachments} isUserMessage={false} />
                )}
                {message.createdAt && <MessageTimestamp timestamp={message.createdAt} />}
              </div>
            </div>
          )
        }
        return null
      })}
      {currentConversation.isAwaitingResponse && (
        <div className={`${classes.botMessageWrapper}`}>
          <div className={`${classes.messageItemWrapper} ${classes.botMessageItem}`}>
            <BotTypingIndicator />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}

export default ConversationHistory
